import { CompanyProxy, CompanyAccess, CompanyAccessProxy, ModuleProxy, PermissionScope } from "../main/graphql/types";
import { AllModulesTagName } from "@me-team/common/src/utils/enums";

interface Role {
    id: number;
    isOwner?: boolean;
}

interface EmployeeAccessLimitRef {
    tariffBlocking?: boolean | null;
    role?: Role | null;
}

interface AccessRoleRef {
    id: number;
    name?: string | null;
    isOwner?: boolean | null;
}

type RoleAccessEntry = Pick<CompanyAccessProxy, "action" | "scope">;
type RoleAccessMap = Map<CompanyAccess, Set<PermissionScope>>;

export default class AccessService {
    get isPremium(): boolean {
        return this._isPremium;
    }

    get isOwner(): boolean {
        return this._currentBranchRole?.isOwner ?? false;
    }

    get hasRole(): boolean {
        return this._currentBranchRole !== null;
    }

    static instance: AccessService;

    private _isPremium: boolean = false;
    private _modulesList: ModuleProxy[] = [];
    private _hasModeratorRole: boolean = false;
    private _companyBusinessType: string | null = null;

    // role-access foundation for the current branch only.
    private _currentBranchId: number | null = null;
    private _currentBranchRole: AccessRoleRef | null = null;
    private _currentRoleAccessList: RoleAccessMap = new Map();
    private _hasCurrentRoleAccessList: boolean = false;
    private _isRoleAccessReadyValue: boolean = false;
    private _isRoleAccessLoadingValue: boolean = false;
    private _roleAccessVersion: number = 0;

    static getInstance(): AccessService {
        if (!AccessService.instance) {
            AccessService.instance = new AccessService();
        }
        return AccessService.instance;
    }

    public init(isPremiumOn: CompanyProxy["isPremiumOn"]): void {
        this._isPremium = isPremiumOn;
    }

    setBookingModerator(roles: Array<string> | undefined): void {
        this._hasModeratorRole = !!roles?.some((role) => role === "ROLE_RESTAURANT_BOOKING_MODERATOR");
    }

    get hasModeratorRole(): boolean {
        return this._hasModeratorRole;
    }

    canAddEmployee(employees: EmployeeAccessLimitRef[] = [], employeeIsOwner: boolean): boolean {
        if (this.isPremium) return true;

        let ownerCounter = 0;
        let otherEmployeeCounter = 0;
        employees.forEach((employee) => {
            if (!employee.tariffBlocking) {
                if (employee.role?.isOwner) {
                    ownerCounter++;
                } else {
                    otherEmployeeCounter++;
                }
            }
        });

        const isOwnerLimitReached = employeeIsOwner && ownerCounter >= 1;
        const isOtherLimitReached = !employeeIsOwner && otherEmployeeCounter >= 6;

        return !isOwnerLimitReached && !isOtherLimitReached;
    }

    public setModules(modulesList: ModuleProxy[]): void {
        this._modulesList = modulesList;
    }

    public getModuleByName(name: string): ModuleProxy | undefined {
        return this._modulesList.find((m) => m.name === name);
    }

    public isModuleActive(moduleName: AllModulesTagName): boolean {
        return this._modulesList.some((module) => module.name === moduleName && module.active);
    }

    public areModulesActive(moduleNames: AllModulesTagName[]): boolean {
        return moduleNames.every((name) => this.isModuleActive(name));
    }

    public setCompanyBusinessType(type: string): void {
        this._companyBusinessType = type;
    }

    public getCompanyBusinessType(): string | null {
        return this._companyBusinessType;
    }

    public beginRoleAccessReload(branchId: number | null): number {
        this._roleAccessVersion += 1;
        this._currentBranchId = branchId;
        this._currentBranchRole = null;
        this._currentRoleAccessList = new Map();
        this._hasCurrentRoleAccessList = false;
        this._isRoleAccessReadyValue = false;
        this._isRoleAccessLoadingValue = branchId !== null;

        return this._roleAccessVersion;
    }

    public setCurrentBranchRole(role?: Partial<AccessRoleRef> | null, branchId?: number, version?: number): void {
        if (this.shouldIgnoreRoleAccessUpdate({ branchId, version })) return;

        const normalizedRole = this.normalizeRole(role);

        if (!normalizedRole) {
            this._currentBranchRole = null;
            this._currentRoleAccessList = new Map();
            this._hasCurrentRoleAccessList = true;
            this._isRoleAccessReadyValue = true;
            this._isRoleAccessLoadingValue = false;
            return;
        }

        this._currentBranchRole = normalizedRole;
        this._isRoleAccessReadyValue = this._hasCurrentRoleAccessList;
    }

    public setCurrentRoleAccessList(
        accesses?: RoleAccessEntry[] | null,
        params?: {
            branchId?: number;
            roleId?: number;
            version?: number;
        }
    ): void {
        if (this.shouldIgnoreRoleAccessUpdate(params)) return;

        if (!this._currentBranchRole && typeof params?.roleId === "number") {
            this._currentBranchRole = { id: params.roleId };
        }

        if (
            typeof params?.roleId === "number" &&
            typeof this._currentBranchRole?.id === "number" &&
            this._currentBranchRole.id !== params.roleId
        ) {
            return;
        }

        this._currentRoleAccessList = this.buildRoleAccessMap(accesses);
        this._hasCurrentRoleAccessList = true;
        this._isRoleAccessReadyValue = true;
        this._isRoleAccessLoadingValue = false;
    }

    public isRoleAccessReady(): boolean {
        return this._isRoleAccessReadyValue;
    }

    public isRoleAccessLoading(): boolean {
        return this._isRoleAccessLoadingValue;
    }

    // checks a single permission
    public hasPermission(action: CompanyAccess, options?: { scope?: PermissionScope }): boolean {
        const scopes = this._currentRoleAccessList.get(action);
        if (!scopes?.size) return false;
        if (!options?.scope) return true;

        if (scopes.has(PermissionScope.All)) {
            return true;
        }

        return scopes.has(options.scope);
    }

    // checks if at least one permission from the list exists
    public hasAnyPermission(actions: CompanyAccess[], options?: { scope?: PermissionScope }): boolean {
        return actions.some((action) => this.hasPermission(action, options));
    }

    // checks if all permissions from the list exist
    public hasAllPermissions(actions: CompanyAccess[], options?: { scope?: PermissionScope }): boolean {
        return actions.every((action) => this.hasPermission(action, options));
    }

    private normalizeRole(role?: Partial<AccessRoleRef> | null): AccessRoleRef | null {
        const roleId = role?.id;
        if (typeof roleId !== "number") return null;

        return {
            id: roleId,
            name: role?.name ?? null,
            isOwner: role?.isOwner ?? null,
        };
    }

    private normalizePermissionScope(scope?: PermissionScope | null): PermissionScope {
        return scope ?? PermissionScope.All;
    }

    private buildRoleAccessMap(accesses?: RoleAccessEntry[] | null): RoleAccessMap {
        const nextAccessMap: RoleAccessMap = new Map();

        accesses?.forEach((access) => {
            if (!access?.action) return;

            const normalizedScope = this.normalizePermissionScope(access.scope);
            const existingScopes = nextAccessMap.get(access.action) ?? new Set<PermissionScope>();
            existingScopes.add(normalizedScope);
            nextAccessMap.set(access.action, existingScopes);
        });

        return nextAccessMap;
    }

    private shouldIgnoreRoleAccessUpdate(params?: { branchId?: number; version?: number }): boolean {
        if (typeof params?.version === "number" && params.version !== this._roleAccessVersion) {
            return true;
        }
        if (typeof params?.branchId !== "number") {
            return false;
        }
        if (this._currentBranchId === null) {
            this._currentBranchId = params.branchId;
            return false;
        }

        return this._currentBranchId !== params.branchId;
    }
}
